import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import toastOptions from "../../config/toastOptions";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { login, resetLoginState } from "../../redux/auth/authSlice";
import CustomInput from "../common/customInput";
import { useTranslation } from "react-i18next";
import { LuEye, LuEyeOff } from "react-icons/lu";

const LoginForm = () => {
  const { t } = useTranslation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isPassword, setIsPassword] = useState(true);

  const dispatch = useDispatch();
  const { setAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from =
    location.state?.from?.pathname &&
    location.state?.from?.pathname !== "/login"
      ? location.state?.from?.pathname
      : "/";
  const ToastOptions = useMemo(() => toastOptions, []);

  const authReducer = useSelector((state) => state.auth);
  const { state, auth: stateAuth } = authReducer.login;

  useEffect(() => {
    if (state.success) {
      setAuth(stateAuth);
      toast.success(t("nav.loginM"), ToastOptions);
      navigate(from, { replace: true });
      dispatch(resetLoginState());
    }
  }, [
    ToastOptions,
    from,
    navigate,
    setAuth,
    state.success,
    stateAuth,
    t,
    dispatch,
  ]);

  useEffect(() => {
    if (state.error) {
      toast.error(state.error, ToastOptions);
      dispatch(resetLoginState());
    }
  }, [ToastOptions, state.error, dispatch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (email && password) {
      dispatch(login({ email: email.trim(), password }));
    }
  };

  useEffect(() => {
    dispatch(resetLoginState());
  }, [dispatch]);

  return (
    <section className="flex justify-center items-center h-[100dvh] gap-3 bg-[--bg-1] overflow-visible">
      <div className="hidden lg:flex lg:flex-1 relative">
        <img
          className="rounded-2xl w-full object-contain self-start"
          src="/assets/login_img.png"
          alt=""
        />
        <span className="w-[.5px] h-[75%] bg-[var(--decor-1)] absolute right-0 self-center"></span>
      </div>

      <div className="flex w-full h-full flex-1 min-w-64 items-center justify-center relative">
        <div className="w-full max-w-[30rem] pb-12">
          <h2 className="font-bold text-4xl text-center text-[--gr-9]">
            {t("login.login")}
          </h2>
          <form onSubmit={handleSubmit} className="flex flex-col gap-5 mt-8">
            <div>
              <label
                htmlFor="email"
                className="block font-medium text-sm text-[var(--tx-1)]"
              >
                {t("login.email")}
              </label>
              <CustomInput
                type="text"
                id="email"
                className="w-full p-3 text-[var(--tx-1)] border-sm rounded-full border-solid border-[var(--decor-1)] text-sm"
                name="email"
                placeholder={t("login.enterE")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={state.loading}
                autoComplete="username"
                inputMode="email"
              />
            </div>
            <div>
              <label
                htmlFor="password"
                className="block font-medium text-sm text-[var(--tx-1)]"
              >
                {t("login.pass")}
              </label>
              <div className="flex items-center relative">
                <CustomInput
                  id="password"
                  className="w-full p-3 pr-12 text-[var(--tx-1)] border-sm rounded-full border-solid border-[var(--decor-1)] text-sm"
                  type={isPassword ? "password" : "text"}
                  name="password"
                  placeholder={t("login.enterP")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={state.loading}
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="text-[--gr-9] text-2xl absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer bg-transparent flex items-center"
                  onClick={() => setIsPassword(!isPassword)}
                  aria-label={
                    isPassword
                      ? t("common.showPassword")
                      : t("common.hidePassword")
                  }
                  aria-pressed={!isPassword}
                >
                  {isPassword ? (
                    <LuEye aria-hidden="true" />
                  ) : (
                    <LuEyeOff aria-hidden="true" />
                  )}
                </button>
              </div>
            </div>
            <Link
              className="text-[#656ED3] text-right"
              to="http://forum.liwasoft.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("login.forget")}
            </Link>

            <button
              type="submit"
              disabled={state.loading}
              className="bg-[#656ED3] text-[var(--tx-4)] h-12 hover:bg-[#656ed3e9] duration-300 rounded-full disabled:opacity-70"
            >
              {state.loading ? (
                <div
                  role="status"
                  className="p-2 flex items-center justify-center"
                >
                  <svg
                    aria-hidden="true"
                    className="w-5 h-5 text-[var(--tx-4)] animate-spin fill-blue-600"
                    viewBox="0 0 100 101"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                      fill="currentColor"
                    />
                    <path
                      d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                      fill="currentFill"
                    />
                  </svg>
                  <span className="sr-only">{t("common.loading")}</span>
                </div>
              ) : (
                t("login.login")
              )}
            </button>
          </form>
        </div>

        <span className="w-[16rem] h-16 bg-[var(--decor-1)] rounded-full absolute -top-48 left-0 -rotate-40"></span>
        <span className="w-[40rem] h-24 bg-[var(--decor-1)] rounded-full absolute -bottom-[17rem] -right-16 -rotate-40 z-[1]"></span>
        <span className="w-[50rem] h-32 bg-[#B1DBDB] rounded-full absolute -bottom-[26rem] -right-20 -rotate-40 opacity-50 z-[2]"></span>
      </div>
    </section>
  );
};

export default LoginForm;
